/**
 * The icons the client screens need, drawn here rather than installed.
 *
 * The hi-fi references an icon set (`icons.jsx`) that was never exported, and
 * its README says to draw them "stroke-based on a 16/20/24 grid in one
 * consistent style". So these are: one 24-unit box, 1.75 stroke, round caps and
 * joins, no fills. Only the ones a screen actually renders — an icon with no
 * screen is untested weight (`client-app-direction` D5). The daily loop needed
 * four; `client-week-and-plan` adds the fifth, `arrow-right`, for moving a week
 * forward.
 *
 * `flame` is deliberately absent. The README says it is used only by the streak,
 * and the owner has parked streaks; drawing it would be drawing the thing we
 * were told not to build.
 *
 * Every one of them is `aria-hidden`. Not one carries meaning on its own: each
 * sits beside a word that says the same thing, because a control whose meaning
 * is only a shape is a control some readers do not have.
 */

interface IconProps {
  size?: number;
  className?: string;
}

function Svg({ size = 24, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export function ArrowLeft(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </Svg>
  );
}

/**
 * Forward, for the one thing in the product that moves forward: the next week.
 *
 * It arrives with `client-week-and-plan`, which is the first screen with a
 * direction to travel in. Like `ArrowLeft` it sits beside a word — "Semana
 * siguiente" — and never alone, because a control whose meaning is only a shape
 * is a control some readers do not have.
 */
export function ArrowRight(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </Svg>
  );
}

export function Check(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m20 6-11 11-5-5" />
    </Svg>
  );
}

export function ChevronRight(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m9 18 6-6-6-6" />
    </Svg>
  );
}

export function Play(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M7 4.5v15l12-7.5-12-7.5Z" />
    </Svg>
  );
}
